/**
 * "For club leaders" copy (#3125, child of the #3122 marketing epic).
 *
 * WHY THIS IS A DATA MODULE AND NOT INLINE JSX
 * --------------------------------------------
 * Same reason as `landing.ts`: `scripts/check-voice.mjs` scans
 * `frontend/src/data/content/*.ts`, and the gallery round-trip edits copy here.
 * Copy written inline in the page is outside both. Keeping it here means the
 * voice gate and the editable proof are real for this page from the first
 * commit.
 *
 * THE ™ IS NOT IN THIS FILE, DELIBERATELY.
 * Core Rule 24 gives the page exactly one mark, on its most prominent use: the
 * hero subhead. The template renders `ORO move<sup>™</sup> {{hero.subAfterBrand}}`,
 * so the mark stays out of the copy string. Every other mention ("New to ORO
 * move?", the header, the footer) is the bare string.
 *
 * reality.lines is an ARRAY OF OBJECTS, not bare strings, on purpose: the
 * gallery `ts-object` adapter round-trips `lines.0.text` (a keyed property) but
 * cannot write back a bare `'string',` array element (no key to anchor the
 * rewrite). Keep the `{ text }` shape so the editable proof applies cleanly.
 *
 * DEMO DATA ONLY. The "See it work" block shows the product using the seeded
 * Golden Hour club, reused from `landing.ts` so the two public pages show the
 * same club. Never a real club, never a member's name or face (#3122).
 *
 * ANCHOR: "Ten minutes before" (#3122). The argument, not the instructions:
 * `/getting-started` is the how-to; this page is the why. Copy owned by the
 * repo owner via the gallery round-trip.
 */

export const LEADERS = {
  /** S1 — hero. */
  hero: {
    headline: 'Running your club shouldn’t be painful.',
    // Rendered as `ORO move™ {subAfterBrand}` by the template (the ™ lives there).
    subAfterBrand:
      'gives your club a place to call home, with events, RSVPs, announcements, conversations, check-ins and more.',
    primaryCta: 'See how it works',
  },

  /** S2 — the reality. Recognition before solution. */
  reality: {
    eyebrow: 'you know the drill',
    lines: [
      { text: 'You make the plan.' },
      { text: 'You get the word out.' },
      { text: 'You communicate every change.' },
      { text: 'And when someone needs to know what’s going on, they ask you.' },
    ],
    turnLead: 'Everyone sees the event.',
    turnRest: 'You see everything it takes to make it happen.',
  },

  /** S3 — three burdens lifted. Each grounded in a real leader capability. */
  burdens: {
    eyebrow: 'It Gets better',
    title: 'Tools that carry the load',
    items: [
      {
        heading: 'Share the work.',
        body: 'Add co-leaders and run the club together. The plan, the roster and the week’s decisions live where every leader can see and edit them.',
      },
      {
        heading: 'Set it up once.',
        body: 'Set up a recurring event once and the whole season builds itself. Save your meeting spot, start time and check-in rules as defaults, so each one starts ready to go.',
      },
      {
        // Delivery channel is the MEMBER's choice (per-channel club_updates
        // prefs). The SMS leg is Supporter-gated — free clubs get push + email —
        // so the copy says "the way they want to hear it" rather than naming
        // SMS, which would over-promise a paid channel to a free-tier leader.
        heading: 'One message reaches everyone.',
        body: 'Post an announcement and it reaches every member the way they want to hear it, then pins to the top. “New location!” gets seen.',
      },
    ],
  },

  /** S4 — see it work. Seeded Golden Hour demo (parity with landing.ts). */
  seeit: {
    eyebrow: 'See it work',
    title: 'The leader’s view.',
    lede: 'You made the plan on Sunday. Here’s what the club sees, and what it handles for you.',
    eventCap: 'Event',
    eventDay: '14',
    eventMonth: 'Mar',
    eventTitle: 'Saturday Long Ride',
    // Recurring-series indicator on the demo event — the real events/batch
    // feature (weekly), surfaced so the page shows recurrence, not just tells it.
    eventRecurrence: 'Every Saturday',
    eventMeta: '6:40 AM · Coopers Rock lot',
    eventBlurb: 'Thirty-five miles, steady pace up front. We regroup at the top.',
    eventGoing: '12 going',
    checkinCap: 'Check-in',
    checkinCount: '11',
    checkinLabel: 'checked in this morning',
    checkinBlurb: 'Members tap once at the start. The count keeps itself.',
    annCap: 'Announcement',
    annPin: 'Pinned · from you',
    annMsg: 'Lower parking lot is locked! Park up top, the event is updated.',
    annTo: 'Sent to all 34 members',
  },

  /** S6 — a week in the life. Ends on the payoff: you got to go too. */
  week: {
    eyebrow: 'One week',
    title: 'Set it once. Keep everyone up to speed.',
    steps: [
      { when: 'Sunday', what: 'You post Saturday’s ride.', sub: 'A few taps, and it’s on everyone’s calendar.' },
      { when: 'Wednesday', what: 'The lower parking lot is closed.', sub: 'You move the start location. Everyone gets the update.' },
      { when: 'Saturday, 6:40', what: 'People show up.', sub: 'They check themselves in. The count keeps itself.' },
      { when: 'And you', what: 'You rolled out with the group.', sub: 'Pace groups set, nothing to sort at the line.' },
    ],
  },

  /** S8 — close. One price-neutral next step + the link to the how-to. */
  close: {
    title: 'Your next event is a few taps away.',
    body: 'Set it up once. Let the club take it from there.',
    cta: 'Set up your next event',
    // #3448: the App Store note above the badge under the close CTA. The badge
    // is an image (AppStoreBadge); only this line is copy.
    appStoreNote: 'Also on iPhone.',
    guideLead: 'New to ORO move?',
    guideLink: 'Start with the leader guide.',
  },
} as const;
